import { format } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { cn } from '../../lib/cn.js';
import { Button } from './button.js';
import { Calendar } from './calendar.js';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';

/**
 * The field-shaped trigger both pickers wear, exported so `DateTimePicker`
 * wears exactly the same one rather than a copy of it that drifts.
 */
export const pickerTriggerClassName = cn(
  'h-10 w-full justify-start gap-2 rounded-field border border-border-default bg-surface px-3.5',
  'text-left text-sm font-normal text-text hover:border-border-strong hover:bg-surface',
);

export interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  /** Blocks past dates — a hire cannot start yesterday. */
  disablePast?: boolean;
  /** Blocks dates after today — for a date something *was* given, not one it is due. */
  disableFuture?: boolean;
  /**
   * The span the month and year dropdowns offer. Setting either swaps the
   * "September 2026" caption for a pair of dropdowns, which is the only
   * bearable way to reach a date years off: an insurance certificate that
   * runs to 2029 is nine clicks of the next-month arrow away otherwise.
   */
  startMonth?: Date;
  endMonth?: Date;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  disablePast = false,
  disableFuture = false,
  startMonth,
  endMonth,
  disabled,
  className,
  id,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const blocked = [
    ...(disablePast ? [{ before: new Date() }] : []),
    ...(disableFuture ? [{ after: new Date() }] : []),
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(pickerTriggerClassName, !value && 'text-text-subtle', className)}
        >
          <CalendarDays className="opacity-60" />
          {value ? format(value, 'd MMM yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-2">
        <Calendar
          mode="single"
          autoFocus
          selected={value}
          // Opens on the month being edited rather than on today, so a
          // correction lands where the existing date is.
          defaultMonth={value}
          captionLayout={startMonth || endMonth ? 'dropdown' : undefined}
          startMonth={startMonth}
          endMonth={endMonth}
          onSelect={(date) => {
            onChange?.(date);
            setOpen(false);
          }}
          disabled={blocked.length > 0 ? blocked : undefined}
        />
      </PopoverContent>
    </Popover>
  );
}

export interface DateRangePickerProps {
  value?: DateRange;
  onChange?: (range: DateRange | undefined) => void;
  placeholder?: string;
  disablePast?: boolean;
  numberOfMonths?: number;
  className?: string;
  id?: string;
}

/** Pick-up and drop-off in one control — the shape every hire form needs. */
export function DateRangePicker({
  value,
  onChange,
  placeholder = 'Pick-up → drop-off',
  disablePast = true,
  numberOfMonths = 2,
  className,
  id,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);

  const label = value?.from
    ? value.to
      ? `${format(value.from, 'd MMM')} → ${format(value.to, 'd MMM yyyy')}`
      : format(value.from, 'd MMM yyyy')
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn(pickerTriggerClassName, !value?.from && 'text-text-subtle', className)}
        >
          <CalendarDays className="opacity-60" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-2">
        <Calendar
          mode="range"
          autoFocus
          selected={value}
          // `min={1}` stops the first click from completing a same-day range,
          // which would otherwise close the popover before the second click.
          min={1}
          onSelect={(range) => {
            onChange?.(range);
            // Close only on a genuine span; react-day-picker reports
            // `from === to` mid-selection.
            const complete = range?.from && range.to && range.to.getTime() !== range.from.getTime();
            if (complete) setOpen(false);
          }}
          numberOfMonths={numberOfMonths}
          disabled={disablePast ? { before: new Date() } : undefined}
        />
      </PopoverContent>
    </Popover>
  );
}
